import { type ReactNode, useId } from 'react'
import { PageSection } from '@/features/jobs/details/components/PageLayout'
import { SectionHeadline } from '@/components/SectionHeadline'
import { match } from '@/utils/pattern-match-util'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import {
  CardItemContent,
  CardItemIcon,
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
  StaticCardList,
  StaticCardListItem,
} from '@/components/Card'
import { LabelChip } from '@/components/Chip'
import { formatMoney } from '@/utils/money-util'
import {
  ModalDialog,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import { US_STATES_AND_TERRITORIES } from '@/assets/data/us-states-territories'
import flattenChildren from 'react-keyed-flatten-children'

const STATE_DISPLAY_NAMES = new Map(US_STATES_AND_TERRITORIES)

export type TaxFilingOutcome = 'refund' | 'balance-due' | 'both'

export interface TaxReturnAmountsProps {
  children?: ReactNode
  outcome: TaxFilingOutcome
}

/**
 * Section for tax return amounts.
 */
export function TaxReturnAmountsSection({
  children,
  outcome,
}: TaxReturnAmountsProps) {
  const headlineId = useId()
  return (
    <PageSection testId="tax-return-amounts-section">
      <SectionHeadline id={headlineId}>
        {match(outcome, {
          refund: () => 'Your tax refund',
          'balance-due': () => 'Your balance due',
          both: () => 'Your refund and balance due',
        })}
      </SectionHeadline>
      <CardList aria-labelledby={headlineId}>{children}</CardList>
    </PageSection>
  )
}

interface TaxReturnYearAmountListItemProps {
  testId?: string
  onAction?: () => unknown
  /**
   * The tax year.
   */
  taxYear: string
  /**
   * Total balance for the tax year. Positive = refund, negative = balance due.
   */
  totalBalanceUsd: number
  /**
   * The state codes in which there are state returns.
   */
  stateCodes: string[]
}

/**
 * Notes Card List Item
 */
export function TaxReturnYearAmountListItem({
  onAction,
  taxYear,
  totalBalanceUsd,
  stateCodes,
  testId,
}: TaxReturnYearAmountListItemProps) {
  const isSpacious = useIsSpacious()
  const outcome = totalBalanceUsd < 0 ? 'balance due' : 'refund'
  const textValue = `Tax year ${taxYear}, ${outcome}: ${formatMoney(Math.abs(totalBalanceUsd))}`

  return (
    <CardListItem onAction={onAction} textValue={textValue} testId={testId}>
      <CardItemContent
        icon={isSpacious && <CardItemIcon name="file" />}
        isInteractive={Boolean(onAction)}
        extras={
          totalBalanceUsd < 0 ? (
            <LabelChip
              text={`${formatMoney(Math.abs(totalBalanceUsd))} due`}
              variant="danger"
            />
          ) : (
            <LabelChip
              text={`${formatMoney(Math.abs(totalBalanceUsd))} refund`}
              variant="default"
            />
          )
        }
      >
        <CardTitle>Tax year {taxYear}</CardTitle>
        <CardSubtitle variant="label">
          {stateCodes.length
            ? `States: ${stateCodes.join(', ')}`
            : 'No state returns'}
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

interface TaxReturnYearOutcomeModalProps {
  children?: ReactNode
  federalBalanceUsd: number
  isOpen: boolean
  onClose: () => unknown
  taxYear: string
}

/**
 * Modal displaying a tax return year's outcome.
 */
export function TaxReturnYearOutcomeModal({
  children,
  federalBalanceUsd,
  isOpen,
  onClose,
  taxYear,
}: TaxReturnYearOutcomeModalProps) {
  const testId = 'tax-return-year-outcome-modal'
  const headingId = useId()
  const federalHeadingId = `${headingId}-federal`
  const stateHeadingId = `${headingId}-state`
  const hasStateReturns = flattenChildren(children).length > 0

  return (
    <ModalDialog isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader textAlign="left">
        <ModalDialogTitle>Tax year {taxYear}</ModalDialogTitle>
        <ModalDialogDescription>
          A breakdown of your federal and state returns for this tax year.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        <SectionHeadline id={federalHeadingId}>Federal</SectionHeadline>
        <StaticCardList aria-labelledby={federalHeadingId}>
          <StaticCardListItem testId={`${testId}-federal-return`}>
            <CardItemContent
              align="center"
              spacingY="base"
              extras={<OutcomeBalanceChip balanceUsd={federalBalanceUsd} />}
            >
              <CardTitle>Federal return</CardTitle>
            </CardItemContent>
          </StaticCardListItem>
        </StaticCardList>

        {hasStateReturns && (
          <div className="pt-2xl">
            <SectionHeadline id={stateHeadingId}>State returns</SectionHeadline>
            <StaticCardList aria-labelledby={stateHeadingId}>
              {children}
            </StaticCardList>
          </div>
        )}
      </ModalDialogContent>
      <ModalDialogFooter>
        <ButtonGroup stretch>
          <Button label="Close" variant="outline" onPress={onClose} />
        </ButtonGroup>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface TaxReturnYearOutcomeModalStateReturnItemProps {
  stateCode: string
  stateBalanceUsd: number
}

/**
 * Tax Return Year Modal State Return Item
 */
export function TaxReturnYearOutcomeModalStateReturnItem({
  stateCode,
  stateBalanceUsd,
}: TaxReturnYearOutcomeModalStateReturnItemProps) {
  const stateName = STATE_DISPLAY_NAMES.get(stateCode) ?? stateCode

  return (
    <StaticCardListItem>
      <CardItemContent
        align="center"
        spacingY="base"
        extras={<OutcomeBalanceChip balanceUsd={stateBalanceUsd} />}
      >
        <CardTitle>{stateName} return</CardTitle>
      </CardItemContent>
    </StaticCardListItem>
  )
}

interface OutcomeBalanceChipProps {
  balanceUsd: number
}

/**
 * Chip showing a return's balance as a refund or amount due.
 */
function OutcomeBalanceChip({ balanceUsd }: OutcomeBalanceChipProps) {
  return balanceUsd < 0 ? (
    <LabelChip
      text={`${formatMoney(Math.abs(balanceUsd))} due`}
      variant="danger"
    />
  ) : (
    <LabelChip
      text={`${formatMoney(Math.abs(balanceUsd))} refund`}
      variant="default"
    />
  )
}
